import { LANGUAGE_TO_LANGUAGE_HEADER } from '../localization/languageCodes';
import { i18n, t } from '../localization';

export const toLocaleDateString = (
  date,
  options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }
) => {
  if (!date) {
    return null;
  }
  try {
    const locale = LANGUAGE_TO_LANGUAGE_HEADER[i18n.locale];
    return new Date(date).toLocaleDateString(locale, options);
  } catch (e) {
    return new Date(date).toLocaleDateString(options);
  }
};

const DURATION_UNITS = [
  ['day', 86400],
  ['hour', 3600],
  ['minute', 60],
  ['second', 1],
];

/**
 * A length of time given in seconds, in the largest unit that keeps it whole
 * and in the app's language: 600 → "10 minutes", 1209600 → "14 days". The
 * unit names are ours, not Intl's: CLDR's Norwegian and Swedish duration of
 * days is "døgn"/"dygn", and we say "dager"/"dagar".
 */
export const formatDuration = (seconds) => {
  const [unit, size] = DURATION_UNITS.find(
    ([, unitSeconds]) => seconds % unitSeconds === 0
  );

  return t(`app:globals:duration:${unit}`, { count: seconds / size });
};

const daysBetween = (date1, date2) => {
  const timeDiff = date2.getTime() - date1.getTime();
  var days = timeDiff / (1000 * 3600 * 24);
  return Math.floor(days);
};

// "kl 10:15"
export const humanFriendlyTime = (date) => {
  const time = new Date(date).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return t('app:globals:fromDate:time', { time });
};

export const humanFriendlyDate = (date, includeTimestamp) => {
  const today = new Date();
  const theDate = new Date(date);
  today.setHours(0, 0, 0, 0);

  const dayDiff = daysBetween(today, theDate);
  const absDayDiff = Math.abs(dayDiff);

  const isToday = dayDiff === 0;

  const isYesterday = dayDiff === -1;
  const isLessThan7DaysAgo = dayDiff < 1 && absDayDiff < 7;

  const isTomorrow = dayDiff === 1;
  const isLessThan7DaysUntil = dayDiff > 1 && dayDiff < 7;

  const timepart = includeTimestamp ? humanFriendlyTime(theDate) : '';

  if (isToday) {
    return `${t('app:globals:fromDate:today')} ${timepart}`;
  }
  if (isYesterday) {
    return `${t('app:globals:fromDate:yesterday')} ${timepart}`;
  }
  if (isTomorrow) {
    return `${t('app:globals:fromDate:tomorrow')} ${timepart}`;
  }
  if (isLessThan7DaysAgo) {
    return `${t('app:globals:fromDate:daysAgo', {
      days: absDayDiff,
    })} ${timepart}`;
  }
  if (isLessThan7DaysUntil) {
    return `${t('app:globals:fromDate:daysUntil', {
      days: absDayDiff,
    })} ${timepart}`;
  }
  return `${toLocaleDateString(theDate)} ${timepart}`;
};

// "Torsdag 8. oktober"; the year is added when it isn't the current one.
export const toWeekdayDateString = (date) => {
  const theDate = new Date(date);
  const day = toLocaleDateString(theDate, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(theDate.getFullYear() !== new Date().getFullYear() && {
      year: 'numeric',
    }),
  });
  return day.charAt(0).toUpperCase() + day.slice(1);
};

export const getLocalizedMonthName = (
  monthNumber,
  long,
  capitalize = false
) => {
  let objDate = new Date();
  objDate.setDate(1);
  objDate.setMonth(monthNumber);

  let month = toLocaleDateString(objDate, { month: long ? 'long' : 'short' });

  if (capitalize) {
    month = month.charAt(0).toUpperCase() + month.slice(1);
  }

  return month;
};

export const addMonths = (date, months) => {
  var d = date.getDate();
  const newDate = new Date(date);
  newDate.setMonth(newDate.getMonth() + +months);
  if (newDate.getDate() != d) {
    newDate.setDate(0);
  }
  return newDate;
};

export const isSameYearAndMonth = (d1, d2) => {
  return (
    d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth()
  );
};

/**
 * Converts a period string to a JS Date.
 * We get period strings from the API in a "YYYYMM" format.
 * @param {string} period
 */

export const periodToDate = (period) => {
  const date = new Date(
    period.toString().substring(0, 4),
    parseInt(period.toString().substring(4), 10),
    0
  );
  return date;
};

export const toLocalDate = (date) => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * The last month reconciled in periodic reporting: if today's calendar day
 * is on or after the 10th, that's the previous month; otherwise it's the
 * month before that.
 * @param {Date} today
 */
export const lastReconciledDate = (today = new Date()) => {
  const currentDate = toLocalDate(today);

  if (currentDate.getDate() >= 10) {
    return toLocalDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
    );
  }
  return toLocalDate(
    new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 0)
  );
};

export const lastReconciledMonthRange = (today = new Date()) => {
  const endDate = lastReconciledDate(today);
  const startDate = toLocalDate(
    new Date(endDate.getFullYear(), endDate.getMonth(), 1)
  );
  return { startDate, endDate };
};

export const MONTHS = Array(12)
  .fill('')
  .map((_, i) => getLocalizedMonthName(i, true));

export const isInDateRange = (date, startDate, endDate) => {
  const targetDate = toLocalDate(new Date(date));
  const start = toLocalDate(new Date(startDate));
  const end = toLocalDate(new Date(endDate));

  return targetDate >= start && targetDate <= end;
};

export const humanFriendlyDuration = (startAt, endAt) => {
  if (!startAt || !endAt) {
    return null;
  }

  const startDate = new Date(startAt);
  const endDate = new Date(endAt);

  const isValidDuration =
    !Number.isNaN(startDate.getTime()) &&
    !Number.isNaN(endDate.getTime()) &&
    endDate >= startDate;

  if (!isValidDuration) {
    return null;
  }

  const durationMinutes = Math.round((endDate - startDate) / (1000 * 60));

  if (durationMinutes < 60) {
    return `${durationMinutes} min`;
  }

  return `${Math.floor(durationMinutes / 60)} t${
    durationMinutes % 60 ? ` ${durationMinutes % 60} min` : ''
  }`;
};
